import { cva, type VariantProps } from 'class-variance-authority';
import type { ComponentPropsWithRef } from 'react';
import { cn } from '../../lib/cn.ts';
import type { ButtonType } from '../../lib/buttonType.ts';

const iconButtonVariants = cva(
  'inline-flex cursor-pointer items-center justify-center shrink-0 transition disabled:opacity-40 disabled:pointer-events-none aria-disabled:opacity-40 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ink-faint',
  {
    variants: {
      variant: {
        surface: 'bg-surface border border-line text-ink hover:bg-fill-faint',
        quiet: 'text-ink-secondary hover:bg-fill hover:text-ink',
        danger: 'text-ink-muted hover:bg-danger-fill hover:text-danger',
        /* An icon-only control that is switched on — the mic during a call. */
        accent: 'bg-accent text-on-accent hover:shadow-glow',
      },
      size: {
        sm: 'size-7',
        md: 'size-9',
        lg: 'size-10.5',
      },
      shape: {
        circle: 'rounded-full',
        square: 'rounded-md',
      },
      /* A 44px tap square on a phone; only alone, or it steals a neighbour's taps. */
      touch: {
        true: 'relative max-mobile:before:absolute max-mobile:before:top-1/2 max-mobile:before:left-1/2 max-mobile:before:size-11 max-mobile:before:-translate-1/2',
        false: '',
      },
    },
    defaultVariants: {
      variant: 'quiet',
      size: 'md',
      shape: 'circle',
      touch: false,
    },
  },
);

interface IconButtonProps
  extends
    Omit<ComponentPropsWithRef<'button'>, 'style' | 'aria-label' | 'type'>,
    VariantProps<typeof iconButtonVariants> {
  type?: ButtonType;
  /* Required at the type level: an icon-only button has no visible text. */
  'aria-label': string;
}

function IconButton({
  className,
  variant,
  size,
  shape,
  touch,
  type = 'button',
  ...props
}: IconButtonProps) {
  return (
    <button
      data-slot='icon-button'
      type={type}
      className={cn(iconButtonVariants({ variant, size, shape, touch }), className)}
      {...props}
    />
  );
}

export { IconButton, iconButtonVariants };
export type { IconButtonProps };
export type IconButtonVariants = VariantProps<typeof iconButtonVariants>;
